import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';
import { Volleyball, CreditCard, CheckCircle, ArrowLeft, Phone, UploadSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DropInFem() {
  const { t } = useTranslation();
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({ name: '', phone: '', preferred_date: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [regId, setRegId] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const handlePayWithCard = async () => {
    setLoading(true);
    try {
      const { data: reg } = await axios.post(`${API}/drop-in-fem/register`, formData);
      const originUrl = window.location.origin;
      const { data: checkout } = await axios.post(`${API}/drop-in-fem/checkout`, { registration_id: reg.id, origin_url: originUrl });
      if (checkout.url) window.location.assign(checkout.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.errorProcessing'));
      setLoading(false);
    }
  };

  const handlePayWithZelle = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/drop-in-fem/register`, {
        ...formData,
        notes: (formData.notes ? formData.notes + ' | ' : '') + 'Pagamento via Zelle'
      });
      setRegId(data.id);
      setStep('zelle-info');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.errorProcessing'));
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptUpload = async (file) => {
    if (!file || !regId) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/drop-in-fem/zelle-receipt?registration_id=${regId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('common.receiptSent'));
      setStep('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.errorProcessing'));
    } finally {
      setUploadingReceipt(false);
    }
  };

  const isValid = formData.name && formData.phone && formData.preferred_date;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-white border-b border-[#E6E2D8] px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-tk.png" alt="Logo" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display='none'; }} />
            <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login" className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium">{t('common.doLogin')}</Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {step === 'success' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center">
            <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-white" weight="fill" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] mb-3">{t('dropInFem.confirmedTitle')}</h2>
            <p className="text-[#73706A] mb-6">{t('dropInFem.confirmedSubtitle')}</p>
            <Button onClick={() => { setStep('form'); setFormData({ name: '', phone: '', preferred_date: '', notes: '' }); }} className="bg-[#6A4C93] hover:bg-[#553C7B] text-white">{t('common.newRegistration')}</Button>
          </div>
        )}

        {step === 'zelle-info' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center space-y-6">
            <div className="w-20 h-20 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto">
              <Phone size={40} className="text-white" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">{t('dropIn.zelleTitle')}</h2>
              <p className="text-[#73706A] mt-2">{t('courtRental.zelleHeader')} <strong className="text-[#6A4C93]">$20.00</strong> {t('courtRental.zelleHeaderSuffix')}</p>
            </div>
            <div className="bg-[#F5F2EB] rounded-lg p-6">
              <p className="text-3xl font-bold text-[#1A1A1A] font-['Outfit'] tracking-wider">5618596830</p>
              <p className="text-sm text-[#73706A] mt-2">Tia Karol Sports</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
              <p className="text-sm text-yellow-800"><strong>{t('common.important')}:</strong> {t('common.zelleNoteShort')}</p>
            </div>
            <label className="cursor-pointer block">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => handleReceiptUpload(e.target.files[0])} data-testid="fem-zelle-upload" />
              <Button className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none" disabled={uploadingReceipt}>
                {uploadingReceipt ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <UploadSimple size={20} className="mr-2" />}
                {uploadingReceipt ? t('common.sending') : t('common.uploadReceipt')}
              </Button>
            </label>
          </div>
        )}

        {step === 'form' && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center">
              <div className="w-20 h-20 bg-[#6A4C93] rounded-full flex items-center justify-center mx-auto mb-4">
                <Volleyball size={40} className="text-white" />
              </div>
              <h1 className="font-['Outfit'] text-3xl font-bold text-[#1A1A1A] mb-2">{t('dropInFem.title')}</h1>
              <p className="text-[#73706A]">{t('dropInFem.subtitle')}</p>
              <p className="text-3xl font-bold text-[#6A4C93] font-['Outfit'] mt-3">$20.00</p>
              <p className="text-xs text-[#73706A]">{t('common.card')}: $21.00 (+5% {t('common.tax').toLowerCase()}) | Zelle: $20.00</p>
            </div>

            <div className="bg-white border border-[#E6E2D8] rounded-xl p-6">
              <h2 className="font-['Outfit'] font-semibold text-xl text-[#1A1A1A] mb-4">{t('dropInFem.regDataTitle')}</h2>
              <div className="space-y-4">
                <div>
                  <Label>{t('common.fullName')} *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('dropInFem.yourNamePh')} required data-testid="fem-name" />
                </div>
                <div>
                  <Label>{t('common.phone')} *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder={t('dropInFem.phonePh')} required data-testid="fem-phone" />
                </div>
                <div>
                  <Label>{t('dropInFem.preferredDate')} *</Label>
                  <Input type="date" value={formData.preferred_date} onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })} required data-testid="fem-date" />
                </div>
                <div>
                  <Label>{t('dropInFem.notes')}</Label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input-field min-h-[60px] resize-none" placeholder={t('dropInFem.notesPh')} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button onClick={handlePayWithCard} disabled={!isValid || loading} className="bg-[#6A4C93] hover:bg-[#553C7B] text-white py-4" data-testid="fem-card-btn">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={20} className="mr-2" /> {t('common.card')} $21.00</>}
              </Button>
              <Button onClick={handlePayWithZelle} disabled={!isValid || loading} variant="outline" className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white py-4" data-testid="fem-zelle-btn">
                <Phone size={20} className="mr-2" /> Zelle $20.00
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-[#73706A] hover:text-[#1A1A1A] flex items-center justify-center gap-2">
            <ArrowLeft size={16} /> {t('common.back')}
          </Link>
        </div>
      </main>
    </div>
  );
}
